import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { catchError, EMPTY, finalize, mergeMap, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OperationStateModel } from '@app/features/manufactory/models/daily-plans.model';
import { DailyPlanStateService } from '@app/shared/modules/time-tracker/daily-plan-state.service';
import { TimeTrackerNewService } from '@app/shared/modules/time-tracker/service/time-tracker-new.service';
import { DailyPlansService } from '@features/manufactory/services/daily-plans.service';
import { TIME_TRACKER_ERROR_TRANSLATION_PATH } from '@core/const/manufactory-error-translation-paths';
import { SnackbarService } from '@core/services/snackbar.service';
import { ErrorHandlerService } from '@shared/modules/shared-form/error-handler.service';

const KOVNAR_KIND_IDS = ['0ca50383-a0d6-421b-9df8-231a2194567b', '183a080d-4436-48a9-a517-8b550d6ff16d'];

export enum ManufacturingOperationState {
  Ready = '1',
  Start = '3',
  Continue = '6',
  Pause = '4',
  Stop = '8',
}

@Injectable({
  providedIn: 'root',
})
export class DailyPlanListenerService {
  private readonly timeTrackerService = inject(TimeTrackerNewService);
  private readonly dailyPlanStateService = inject(DailyPlanStateService);
  private readonly dailyPlansService = inject(DailyPlansService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbarService = inject(SnackbarService);
  // ErrorHandlerService is not provided in root
  private readonly errorHandlerService = Injector.create({ providers: [ErrorHandlerService], parent: inject(Injector) }).get(
    ErrorHandlerService,
  );

  constructor() {
    this.timeTrackerService.onStart$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap(({ id, taskNumber, state, revert }) => {
          let stateChanged = false;
          return this.dailyPlanStateService.getCurrentOperationState(id, state).pipe(
            switchMap(({ operation, param }) =>
              this.dailyPlanStateService.updateOperationState(
                operation!.id!,
                this.dailyPlanStateService.getNextOperationState(
                  operation?.state as unknown as OperationStateModel,
                  param ? 'continue' : 'start',
                ),
              ),
            ),
            tap(() => {
              stateChanged = true;
              this.timeTrackerService.doReload(id);
            }),
            catchError(error => this.handleError(error, stateChanged)),
            finalize(() => this.finishAction(id, taskNumber, stateChanged, revert)),
          );
        }),
      )
      .subscribe();

    this.timeTrackerService.onPause$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap(({ id, taskNumber, revert }) => {
          let stateChanged = false;
          return this.dailyPlanStateService.getCurrentOperationState(id).pipe(
            switchMap(({ operation }) =>
              this.dailyPlanStateService.updateOperationState(
                operation!.id!,
                this.dailyPlanStateService.getNextOperationState(operation?.state as unknown as OperationStateModel, 'pause'),
              ),
            ),
            tap(() => {
              stateChanged = true;
              this.timeTrackerService.doReload(id);
              this.timeTrackerService.finishedProcessing(taskNumber);
            }),
            catchError(error => this.handleError(error, stateChanged)),
            finalize(() => this.finishAction(id, taskNumber, stateChanged, revert)),
          );
        }),
      )
      .subscribe();

    this.timeTrackerService.onStop$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap(({ id, taskNumber, revert }) => {
          let stateChanged = false;
          return this.dailyPlanStateService.getCurrentOperationState(id).pipe(
            switchMap(({ operation }) =>
              this.dailyPlanStateService.updateOperationState(
                operation!.id!,
                this.dailyPlanStateService.getNextOperationState(operation?.state as unknown as OperationStateModel, 'stop'),
              ),
            ),
            tap(() => {
              stateChanged = true;
              this.timeTrackerService.doReload(id);
            }),
            switchMap(() => this.dailyPlansService.detail(id)),
            tap(dailyPlan => {
              if (
                KOVNAR_KIND_IDS.includes(dailyPlan.header.internalBatchKindId as string) &&
                dailyPlan.materialBillOfMaterialsPosition.isProductionFinished
              ) {
                this.timeTrackerService.openLocksModal({
                  operationId: dailyPlan.operation.id as string,
                  amountOfManufacturedPieces: dailyPlan.materialBillOfMaterialsPosition.amountOfManufacturedPieces,
                  locks: dailyPlan.materialBillOfMaterialsPosition.locks ?? [],
                });
              }
            }),
            catchError(error => this.handleError(error, stateChanged)),
            finalize(() => this.finishAction(id, taskNumber, stateChanged, revert)),
          );
        }),
      )
      .subscribe();
  }

  /* Keeps the stream alive and tells the user that the operation state was not changed */
  private handleError(error: HttpErrorResponse | string, stateChanged: boolean) {
    const handledByInterceptor = typeof error !== 'string' && [401, 403].includes(error.status);

    if (
      !stateChanged &&
      !handledByInterceptor &&
      !this.errorHandlerService.handleExpected4xxErrors(error, TIME_TRACKER_ERROR_TRANSLATION_PATH)
    ) {
      this.snackbarService.addSnackbarAndTranslateIt({ message: 'common.entity.unexpectedError', iconType: 'error' });
    }

    return EMPTY;
  }

  /* When the state was not changed, the tracker is returned to match the operation and both are reloaded */
  private finishAction(id: string, taskNumber: string, stateChanged: boolean, revert: () => void) {
    if (!stateChanged) {
      revert();
      this.timeTrackerService.cancelLeave();
      this.timeTrackerService.doReload(id);
    }
    this.timeTrackerService.endAction(taskNumber);
  }
}
